import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';

const messages = defineMessages({
  bulkBody: {
    id: 'Team.inviteAccessBanner.bulkBody',
    defaultMessage:
      'Assigning the same access to {count, plural, one {<bold>#<br></br>entity</bold>} other {<bold>#<br></br>entities</bold>}} in your organization.',
    description:
      'Bulk multi-legal-entity invite banner body. The count and entity label render as a bold line break segment.',
  },
  bulkAction: {
    id: 'Team.inviteAccessBanner.bulkAction',
    defaultMessage: 'Customize by entity',
    description:
      'Action link that switches the multi-legal-entity invite from bulk mode to per-entity editing mode.',
  },
  independentBody: {
    id: 'Team.inviteAccessBanner.independentBody',
    defaultMessage: 'Editing entities independently',
    description:
      'Body text for the multi-legal-entity invite banner when the inviter is configuring access per entity.',
  },
  independentAction: {
    id: 'Team.inviteAccessBanner.independentAction',
    defaultMessage: 'Apply changes in bulk',
    description:
      'Action link that switches the multi-legal-entity invite from per-entity mode back to bulk mode.',
  },
  toggleModeAccessibilityLabel: {
    id: 'Team.inviteAccessBanner.toggleModeAccessibilityLabel',
    defaultMessage: 'Switch invite access mode',
    description:
      'Accessibility label for the action that toggles invite access mode between bulk and per-entity editing',
  },
});

export type InviteAccessBannerMode = 'bulk' | 'independent';

export type InviteAccessBannerParams = {
  mode: InviteAccessBannerMode;
  entityCount: number;
  onCustomizeByEntity: () => void;
  onApplyChangesInBulk: () => void;
};

const ACTION_STYLE = { cursor: 'pointer' } as const;

function renderBulkLineBreak() {
  return <br />;
}

function renderBulkBoldText(chunks: ReactNode) {
  return (
    <Text as="span" font="label1" color="fg">
      {chunks}
    </Text>
  );
}

export function InviteAccessBanner({
  mode,
  entityCount,
  onCustomizeByEntity,
  onApplyChangesInBulk,
}: InviteAccessBannerParams) {
  const { formatMessage } = useIntl();

  const bulkText = (
    <Text as="span" font="label2" color="fg">
      {formatMessage(messages.bulkBody, {
        count: entityCount,
        br: renderBulkLineBreak,
        bold: renderBulkBoldText,
      })}
    </Text>
  );
  const independentText = (
    <Text as="span" font="label2" color="fg">
      {formatMessage(messages.independentBody)}
    </Text>
  );

  return (
    <Box background="bgSecondary" borderRadius={200} padding={2} width="100%">
      <HStack alignItems="center" justifyContent="space-between" gap={3} width="100%">
        <HStack alignItems="center" gap={1.5} flexGrow={1} flexShrink={1}>
          <Icon name="corporation" size="s" color="fg" />
          {mode === 'bulk' ? bulkText : independentText}
        </HStack>
        <Pressable
          onClick={mode === 'bulk' ? onCustomizeByEntity : onApplyChangesInBulk}
          background="transparent"
          noScaleOnPress
          flexShrink={0}
          accessibilityLabel={formatMessage(messages.toggleModeAccessibilityLabel)}
        >
          <Text
            as="span"
            font="label2"
            color="fgMuted"
            textDecoration="underline"
            noWrap
            style={ACTION_STYLE}
          >
            {formatMessage(mode === 'bulk' ? messages.bulkAction : messages.independentAction)}
          </Text>
        </Pressable>
      </HStack>
    </Box>
  );
}
